/* eslint-disable no-await-in-loop */
import { useState } from 'react'
import { useBreakpoint, useToast } from '@vtex/admin-ui'
import { useHistory } from 'react-router'
import { useIntl } from 'react-intl'
import { getReduxStore } from '@/checkout-instore-legacy/redux/reduxStore'
import { isEmpty } from 'ramda'
import { clearSavedAttachments } from '@/checkout-instore-legacy/cart/actions'

import { getProductSku } from '../utils/getProductSku'
import { useFeatureFlag, useIsMounted } from '../../../../../core/hooks'
import { useOrderForm } from '../../../../../core/requests/checkout'
import { CheckoutRoute } from '../../../constants'
import type { SavedAttachment } from '../../../../../core/typings'
import { useAssemblyItemsContext } from '../context/AssemblyItemsContext'
import { logger } from '../../../../../logger'
import { getDefaultSellerId } from '../../../utils/seller'

export const useAssemblyItems = (skuId?: string | null) => {
  const { assemblyItemState } = useAssemblyItemsContext()
  const [isBusy, setIsBusy] = useState(false)
  const { breakpoint } = useBreakpoint()
  const isMobile = breakpoint === 'mobile'
  const history = useHistory()
  const showToast = useToast()
  const { formatMessage } = useIntl()
  const { isUseAssemblyOptionsEndpointEnabled } = useFeatureFlag()
  const { addCartAssemblyItem, addCartItem, addItemToCart } = useOrderForm()
  const isMountedRef = useIsMounted()

  const products = getReduxStore()?.getState()?.product.products ?? []
  const savedAttachments: never[] | SavedAttachment =
    getReduxStore()?.getState()?.cart.savedAttachments ?? []

  const productSku = getProductSku(products, skuId ?? '')
  const hasSavedAttachments = !isEmpty(savedAttachments)
  const attachments: any = Array.isArray(savedAttachments)
    ? savedAttachments[0]
    : []

  const handleAddCartItem = async () => {
    try {
      const cartItemResponse = hasSavedAttachments
        ? await addCartItem(
            productSku[0],
            productSku[0].name,
            attachments.item.attachments
          )
        : await addCartItem(productSku[0], productSku[0].name)

      return cartItemResponse.items.findIndex(
        (product) => product.id === productSku[0].id
      )
    } catch (err) {
      throw new Error(String(err))
    }
  }

  const handleAddAssemblyItem = async (
    checkboxStateValue: string,
    index: number,
    assemblyId: string
  ) => {
    try {
      await addCartAssemblyItem(
        checkboxStateValue,
        productSku[0],
        index,
        true,
        assemblyId
      )
    } catch (error) {
      logger.error({
        workflowType: 'add-assembly-items-to-cart',
        error: error instanceof Error ? error : new Error(String(error)),
      })
      showToast({
        message: formatMessage({
          id: 'errorSavingData',
        }),
        variant: 'critical',
        dismissible: true,
      })
    }
  }

  const handleAddAssemblyItemToCart = async () => {
    setIsBusy(true)

    try {
      if (productSku[0]) {
        if (isUseAssemblyOptionsEndpointEnabled()) {
          const index = await handleAddCartItem()

          if (!isEmpty(assemblyItemState.values)) {
            for (const assemblyItemStateValue of assemblyItemState.values) {
              await handleAddAssemblyItem(
                assemblyItemStateValue.id,
                index,
                assemblyItemStateValue.assemblyId
              )
            }
          }
        } else {
          const parentItem = {
            id: productSku[0].id,
            productId: productSku[0].productId,
            name: productSku[0].name,
            quantity: 1,
            seller: productSku[0].sellers[0].id ?? getDefaultSellerId(),
            imageUrl: productSku[0].images[0],
          }

          const selectedAssemblyItems = assemblyItemState.values.map(
            (assemblyItemStateValue) => ({
              id: assemblyItemStateValue.id,
              name: assemblyItemStateValue.name,
              quantity: 1,
              seller: assemblyItemStateValue.seller,
              imageUrl: assemblyItemStateValue.imageUrl,
            })
          )

          await addItemToCart([parentItem, ...selectedAssemblyItems])
        }
      } else {
        logger.info({
          workflowType: 'add-assembly-item-to-cart',
          message: 'Product without SKU no Assembly Options',
          data: { products },
        })
      }

      getReduxStore()?.dispatch(clearSavedAttachments())

      history.push(CheckoutRoute.Cart)
    } catch (err) {
      logger.error({
        workflowType: 'add-assembly-items-to-cart',
        error: err instanceof Error ? err : new Error(String(err)),
      })
      showToast({
        message: formatMessage({
          id: 'errorSavingData',
        }),
        variant: 'critical',
        dismissible: true,
      })
    } finally {
      if (isMountedRef.current) {
        setIsBusy(false)
      }
    }
  }

  return {
    isMobile,
    productSku,
    hasSavedAttachments,
    attachments,
    handleAddAssemblyItemToCart,
    isBusy,
    assemblyItemState,
  }
}
